// ============================================================================
// ORGANISATION SERVICE — Settings → Organisation: entities, their business
// units and financial years. Works without an entity selected (a fresh install
// has none yet).
// ============================================================================

import { get, post, put } from '../client/axiosClient';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import { organisationEndpoints } from '../endpoints/iam.endpoints';
import type {
  ApiResponse, OrganisationDto, OrgLookupsDto, SaveBusinessUnitRequest, SaveEntityRequest, SaveFinancialYearRequest,
  RegistrationNumberParseDto, ApplyCoaTemplateResult,
} from '../types';

type Created = { id: string };

class OrganisationService {
  async get(): Promise<OrganisationDto> {
    const r = await get<ApiResponse<OrganisationDto>>(organisationEndpoints.root());
    return r.data.data;
  }

  async getLookups(): Promise<OrgLookupsDto> {
    const r = await get<ApiResponse<OrgLookupsDto>>(organisationEndpoints.lookups());
    return r.data.data;
  }

  /** What a jurisdiction makes of a registration number as it is typed (the new entity has no jurisdiction yet). */
  async parseRegistration(jurisdictionId: string, number: string): Promise<RegistrationNumberParseDto> {
    const url = buildEndpointWithQuery(organisationEndpoints.parseRegistration(), { jurisdictionId, number });
    const r = await get<ApiResponse<RegistrationNumberParseDto>>(url);
    return r.data.data;
  }

  async createEntity(data: SaveEntityRequest): Promise<string> {
    const r = await post<ApiResponse<Created>>(organisationEndpoints.entities(), data);
    return r.data.data.id;
  }

  /** Applies a chart template to an entity whose chart is still empty. */
  async applyCoaTemplate(entityId: string, templateId: string): Promise<ApplyCoaTemplateResult> {
    const r = await post<ApiResponse<ApplyCoaTemplateResult>>(organisationEndpoints.coaTemplate(entityId), { templateId });
    return r.data.data;
  }

  async updateEntity(id: string, data: SaveEntityRequest): Promise<void> {
    await put(organisationEndpoints.entity(id), data);
  }

  async createBusinessUnit(entityId: string, data: SaveBusinessUnitRequest): Promise<string> {
    const r = await post<ApiResponse<Created>>(organisationEndpoints.businessUnits(entityId), data);
    return r.data.data.id;
  }

  async updateBusinessUnit(id: string, data: SaveBusinessUnitRequest): Promise<void> {
    await put(organisationEndpoints.businessUnit(id), data);
  }

  async createFinancialYear(entityId: string, data: SaveFinancialYearRequest): Promise<string> {
    const r = await post<ApiResponse<Created>>(organisationEndpoints.financialYears(entityId), data);
    return r.data.data.id;
  }

  async updateFinancialYear(id: string, data: SaveFinancialYearRequest): Promise<void> {
    await put(organisationEndpoints.financialYear(id), data);
  }
}

export const organisationService = new OrganisationService();
